import React from 'react';
import numeral from 'numeral';
import { MaxCpm } from '@components/BidStrategy/MaxCpm';
import { EventPicker } from '@components/BidStrategy/EventPicker';
import {
  BidRange,
  GetLockedInfoType,
  GetLockedStatusType,
  GetRecommendedBidRangeType,
  GetUnlockedContentType,
} from './types';
import { BID_STRATEGIES } from './constants';
import { fields } from '../constants';
import { inventoryTypes } from '../Inventory/constants';

const getAdjustedBidRange = (range: number[]): BidRange =>
  range.map(n => n * 1.18 + 0.4) as BidRange;

export const getRecommendedBidRange = ({
  bundles,
  inventoryOption,
  peacock,
  isInternal,
}: GetRecommendedBidRangeType): BidRange => {
  switch (inventoryOption) {
    case inventoryTypes.bundles: {
      return bundles
        .map(
          ({ adjusted_cpm }: { adjusted_cpm: number | null }) =>
            adjusted_cpm ?? 0
        )
        .filter((cpm: number) => !!cpm)
        .sort((a: number, b: number) => a - b) as BidRange;
    }
    case inventoryTypes.premiumDisplay: {
      return [3, undefined];
    }
    case inventoryTypes.premiumReach:
    default: {
      const range = peacock ? (isInternal ? [17, 21] : [18, 21]) : [10, 20];
      return getAdjustedBidRange(range);
    }
  }
};

export const getRangeText = (range: BidRange) => {
  const min = range[0];
  const max = range[range.length - 1];

  if (max === undefined) {
    return `The recommended bid for your inventory selection is ${numeral(
      Math.ceil(min)
    ).format('$0,0')} or higher.`;
  }

  const rangeText =
    min === max
      ? `${numeral(Math.ceil(min)).format('$0,0')}`
      : `${numeral(min).format('$0,0')} - ${numeral(Math.ceil(max)).format(
          '0,0'
        )}`;

  return `The recommended bid range for your inventory selection is ${rangeText}.`;
};

export const getUnlockedContent = ({
  option,
  control,
  bidStrategyEventSet,
  rangeText,
  handleCPMSwitchChange,
  isMaxCPMBidEnabled,
}: GetUnlockedContentType) => {
  switch (option.display_name) {
    case BID_STRATEGIES.MAX_IMPRESSIONS:
    case BID_STRATEGIES.AUTO_BID:
    case BID_STRATEGIES.MAX_ROAS:
      return (
        <MaxCpm
          control={control}
          handleCPMSwitchChange={handleCPMSwitchChange}
          isMaxCPMBidEnabled={isMaxCPMBidEnabled}
          rangeText={rangeText}
          fieldNames={{
            maxCPMBidEnabled: fields.maxCPMBidEnabled.path,
            maxCPMBid: fields.maxCPMBid.path,
          }}
        />
      );
    case BID_STRATEGIES.MANUAL_BID:
      return (
        <MaxCpm
          required
          control={control}
          handleCPMSwitchChange={handleCPMSwitchChange}
          isMaxCPMBidEnabled={isMaxCPMBidEnabled}
          rangeText={rangeText}
          fieldNames={{
            maxCPMBidEnabled: fields.maxCPMBidEnabled.path,
            maxCPMBid: fields.maxCPMBid.path,
          }}
        />
      );
    case BID_STRATEGIES.FIXED_CPM:
      return (
        <MaxCpm
          required
          control={control}
          label="Bid"
          handleCPMSwitchChange={handleCPMSwitchChange}
          isMaxCPMBidEnabled={isMaxCPMBidEnabled}
          rangeText={rangeText}
          fieldNames={{
            maxCPMBidEnabled: fields.maxCPMBidEnabled.path,
            maxCPMBid: fields.maxCPMBid.path,
          }}
        />
      );
    case BID_STRATEGIES.MAX_OUTCOMES:
      return (
        <>
          <EventPicker
            control={control}
            bidStrategyEventSet={bidStrategyEventSet}
            fieldName={fields.adGroupBidStrategyEvent.path}
          />
          <MaxCpm
            control={control}
            handleCPMSwitchChange={handleCPMSwitchChange}
            isMaxCPMBidEnabled={isMaxCPMBidEnabled}
            rangeText={rangeText}
            fieldNames={{
              maxCPMBidEnabled: fields.maxCPMBidEnabled.path,
              maxCPMBid: fields.maxCPMBid.path,
            }}
          />
        </>
      );
    default:
      return null;
  }
};

export const getLockedStatus = ({
  option,
  bidStrategyEventSet,
}: GetLockedStatusType) => {
  switch (option?.display_name) {
    case BID_STRATEGIES.MAX_OUTCOMES:
      return !bidStrategyEventSet || bidStrategyEventSet.length === 0;
    default:
      return false;
  }
};

export const getLockedInfo = ({ option, lockedInfo }: GetLockedInfoType) => {
  switch (option.display_name) {
    case BID_STRATEGIES.MAX_OUTCOMES:
      return lockedInfo.trackingLockedInfo;
    default:
      return null;
  }
};
